import { useState, useRef, useEffect, useMemo } from "react";
import { X, ChevronDown, User as UserIcon, Check, Search } from "lucide-react";
import { cn } from "../../utils/cn";
import { User } from "../../types";

interface UserMultiSelectProps {
    label: string;
    selectedUserIds: string[];
    users: User[];
    onChange?: (userIds: string[]) => void;
    placeholder?: string;
    disabled?: boolean;
}

/**
 * Multi-select dropdown for selecting users
 * Shows selected users as removable pills
 * Includes search functionality and alphabetical sorting
 */
export function UserMultiSelect({
    label,
    selectedUserIds,
    users,
    onChange,
    placeholder = "Select users...",
    disabled = false
}: UserMultiSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Helper to get display name with fallback
    const getDisplayName = (user: User) => user.displayName || user.email || 'Unknown User';

    // Filter out users without a name or email (blank entries) and sort alphabetically
    const validUsers = useMemo(() => {
        return users
            .filter(u => u.displayName || u.email)
            .sort((a, b) => getDisplayName(a).localeCompare(getDisplayName(b)));
    }, [users]);

    // Filter users based on search query
    const filteredUsers = useMemo(() => {
        if (!searchQuery.trim()) return validUsers;
        const query = searchQuery.toLowerCase();
        return validUsers.filter(user =>
            getDisplayName(user).toLowerCase().includes(query) ||
            (user.email && user.email.toLowerCase().includes(query)) ||
            (user.jobTitle && user.jobTitle.toLowerCase().includes(query))
        );
    }, [validUsers, searchQuery]);

    // Get selected user objects (also sorted)
    const selectedUsers = useMemo(() => {
        return validUsers.filter(u => selectedUserIds.includes(u.id));
    }, [validUsers, selectedUserIds]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        if (!isOpen) {
            setSearchQuery(""); // Clear search when closing
        }
    }, [isOpen]);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleUser = (userId: string) => {
        if (!onChange) return;
        if (selectedUserIds.includes(userId)) {
            onChange(selectedUserIds.filter(id => id !== userId));
        } else {
            onChange([...selectedUserIds, userId]);
        }
    };

    const removeUser = (userId: string) => {
        if (!onChange) return;
        onChange(selectedUserIds.filter(id => id !== userId));
    };

    return (
        <div ref={containerRef} className="relative">
            <label className="text-[10px] font-bold uppercase text-slate-500 mb-1 block">
                {label}
            </label>

            {/* Selected Users Pills */}
            <div
                className={cn(
                    "w-full min-h-[38px] flex flex-wrap gap-1.5 p-2 rounded-lg transition-all cursor-pointer",
                    "bg-slate-100 dark:bg-slate-800",
                    disabled ? "cursor-not-allowed opacity-60" : "hover:bg-slate-200 dark:hover:bg-slate-700",
                    isOpen && "ring-2 ring-siemens-petrol"
                )}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedUsers.length === 0 ? (
                    <span className="text-xs text-slate-400">{placeholder}</span>
                ) : (
                    selectedUsers.map(user => (
                        <span
                            key={user.id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-siemens-petrol/10 text-siemens-petrol dark:bg-siemens-petrol-light/10 dark:text-siemens-petrol-light rounded-full text-xs font-medium"
                        >
                            {user.photoUrl ? (
                                <img src={user.photoUrl} alt="" className="w-4 h-4 rounded-full" />
                            ) : (
                                <UserIcon className="w-3 h-3" />
                            )}
                            {getDisplayName(user)}
                            {onChange && !disabled && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeUser(user.id); }}
                                    className="ml-0.5 hover:text-red-500 transition-colors"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </span>
                    ))
                )}
                <ChevronDown className={cn(
                    "w-4 h-4 ml-auto text-slate-400 transition-transform",
                    isOpen && "rotate-180"
                )} />
            </div>

            {/* Dropdown */}
            {isOpen && !disabled && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-slate-200 dark:border-slate-700">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-siemens-petrol dark:focus:ring-siemens-petrol-light text-slate-900 dark:text-white placeholder-slate-400"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* User List */}
                    <div className="max-h-48 overflow-y-auto">
                        {filteredUsers.length === 0 ? (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">
                                {searchQuery ? "No users found matching your search" : "No users available"}
                            </div>
                        ) : (
                            filteredUsers.map(user => {
                                const isSelected = selectedUserIds.includes(user.id);
                                return (
                                    <button
                                        key={user.id}
                                        onClick={() => toggleUser(user.id)}
                                        className={cn(
                                            "w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors",
                                            isSelected && "bg-siemens-petrol/5 dark:bg-siemens-petrol-light/5"
                                        )}
                                    >
                                        {user.photoUrl ? (
                                            <img src={user.photoUrl} alt="" className="w-6 h-6 rounded-full" />
                                        ) : (
                                            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <UserIcon className="w-3 h-3 text-slate-500" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-medium text-slate-900 dark:text-white truncate">
                                                {getDisplayName(user)}
                                            </div>
                                            <div className="text-[10px] text-slate-500 truncate">
                                                {user.jobTitle || user.email}
                                            </div>
                                        </div>
                                        {isSelected && (
                                            <Check className="w-4 h-4 text-siemens-petrol dark:text-siemens-petrol-light flex-shrink-0" />
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserMultiSelect;

